import { ForwardedRef, Fragment, ReactNode, forwardRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'

import { useScrollListener } from '@hooks/scroll-position'
import { Button } from './button'
import { Form } from 'react-final-form'
import { useThemeContext } from '@contexts/theme'

export type ModalProps = {
  isOpen: boolean
  open?: () => void
  close: () => void
  header?: ReactNode
  footer?: ReactNode
  title?: ReactNode
  description?: string
  children?: ReactNode
  inline?: boolean
  fit?: boolean
  fullscreen?: boolean
  className?: string
  modeOverride?: 'light' | 'dark'
}

export function Modal({
  isOpen = false,
  close,
  title,
  header,
  footer,
  description,
  children,
  inline = false,
  fit = false,
  fullscreen = false,
  className,
  modeOverride,
}: ModalProps) {
  const theme = useThemeContext()
  const scrollPosition = useScrollListener()

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={close}>
        {/* backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-[99999] bg-black/60 will-change-[opacity]"
            aria-hidden="true"
          />
        </Transition.Child>
        <div
          className={classNames(
            'fixed flex items-center justify-center inset-0 z-[99999]',
            modeOverride ? modeOverride : theme.mode,
            {
              'p-4': !(fullscreen || inline),
            }
          )}
        >
          <ModalPanel
            close={close}
            title={title}
            header={header}
            footer={footer}
            description={description}
            inline={inline}
            fit={fit}
            fullscreen={fullscreen}
            className={className}
            scrolled={scrollPosition.scrolled}
            atTop={scrollPosition.atTop}
            atBottom={scrollPosition.atBottom}
            ref={scrollPosition.listener}
          >
            {children}
          </ModalPanel>
        </div>
      </Dialog>
    </Transition>
  )
}

export function FormModal<T>({
  initialValues,
  validate,
  onSubmit,
  isOpen = false,
  close,
  title,
  header,
  footer,
  description,
  children,
  inline = false,
  fit = false,
  fullscreen = false,
  className,
}: ModalProps & {
  initialValues?: Partial<T>
  validate?: (vals: T) => Promise<undefined | { [k in keyof T]?: string | undefined }>
  onSubmit: (val: T) => void | Promise<void>
}) {
  const scrollPosition = useScrollListener()

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={close}>
        {/* backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-[99999] bg-black/60 will-change-[opacity]"
            aria-hidden="true"
          />
        </Transition.Child>
        <div
          className={classNames('fixed flex items-center justify-center inset-0 z-[99999]', {
            'p-4': !(fullscreen || inline),
          })}
        >
          <Form onSubmit={onSubmit} validate={validate} initialValues={initialValues}>
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit} className=" contents">
                <ModalPanel
                  close={close}
                  title={title}
                  header={header}
                  footer={footer}
                  description={description}
                  inline={inline}
                  fit={fit}
                  fullscreen={fullscreen}
                  className={className}
                  scrolled={scrollPosition.scrolled}
                  atTop={scrollPosition.atTop}
                  atBottom={scrollPosition.atBottom}
                  ref={scrollPosition.listener}
                >
                  {children}
                </ModalPanel>
              </form>
            )}
          </Form>
        </div>
      </Dialog>
    </Transition>
  )
}

const ModalPanel = forwardRef(function ModalPanel(
  {
    children,
    className,
    inline,
    fullscreen,
    fit,
    header,
    footer,
    title,
    description,
    atTop,
    close,
    atBottom,
    scrolled,
  }: {
    className?: string
    inline: boolean
    fullscreen: boolean
    fit: boolean
    scrolled: boolean
    atTop: boolean
    atBottom: boolean
    header: ReactNode
    footer: ReactNode
    title: ReactNode
    description: ReactNode
    children: ReactNode
    close: ModalProps['close']
  },
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-200"
      enterFrom="opacity-0 scale-105"
      enterTo="opacity-100 scale-100"
      leave="ease-in duration-150"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
    >
      {/* modal foreground */}
      <Dialog.Panel
        as="section"
        ref={ref}
        className={classNames(
          className,
          'relative mx-auto max-h-full overflow-y-auto overflow-x-hidden background-basic text-basic shadow-xl border border-standard-xlight',
          !inline
            ? {
                'w-screen h-screen !border-0': fullscreen,
                'w-full sm:w-fit h-fit rounded-lg max-w-full': fit && !fullscreen,
                'w-full rounded-lg max-w-xs xs:max-w-sm sm:max-w-xl md:max-w-2xl lg:max-w-4xl':
                  !fit && !fullscreen,
              }
            : {}
        )}
      >
        {/* Modal Sticky Header */}
        {(header || title || description) && (
          <div
            className={classNames(
              'transition sticky box-border z-[1] top-0 w-full pb-3 pt-3 sm:pt-6 px-3 sm:pl-6 pr-12 sm:pr-16 background-basic',
              {
                'drop-shadow-[0px_4px_8px_rgb(0,0,0,0)]': !scrolled,
                'drop-shadow-[0px_4px_8px_rgb(0,0,0,0.15)] dark:drop-shadow-[0px_4px_8px_rgb(0,0,0,0.4)]':
                  scrolled,
              }
            )}
          >
            {header}
            {title && (
              <Dialog.Title className="heading-lg sm:heading-xl text-basic">{title}</Dialog.Title>
            )}
            {description && (
              <Dialog.Description className="italic">{description}</Dialog.Description>
            )}
            <ModalCloseButton close={close} />
          </div>
        )}
        {/* Modal Body */}
        <div
          className={classNames(
            { 'px-3 py-3 space-y-4 sm:p-6': !fullscreen && !inline, contents: inline },
            {}
          )}
        >
          {children}
        </div>
        {/* Modal Footer */}
        {footer && (
          <div
            className={classNames(
              'transition sticky z-[1] bottom-0 w-full background-basic border-t px-3 py-3 sm:px-6 sm:pb-6',
              {
                'drop-shadow-[0_-4px_12px_rgb(0,0,0,.11)] dark:drop-shadow-[0_-8px_10px_rgb(0,0,0,0.4)] border-card-dark':
                  !atBottom,
                'drop-shadow-[0px_-4px_12px_rgb(0,0,0,0)] border-transparent': atBottom,
              }
            )}
          >
            {footer}
          </div>
        )}
        {!(header || title || description) && <ModalCloseButton close={close} />}
      </Dialog.Panel>
    </Transition.Child>
  )
})

function ModalCloseButton({ close }: { close: ModalProps['close'] }) {
  return (
    <Button.Floating
      role="close"
      onClick={close}
      sm
      className={classNames(
        'absolute !px-2 !py-1 text-sm font-bold !shadow-none !drop-shadow-none',
        'transition duration-200 ease-in-out border border-transparent rounded-lg outline-none',
        'top-2 right-2 text-basic ring-2 ring-offset-1 ring-transparent focus:ring-primary-500 rounded-md'
      )}
    >
      X
    </Button.Floating>
  )
}
